import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RotateCcw, ClipboardCheck, Users, Key, Car, Package, Clock, AlertTriangle } from "lucide-react";
import logger from "@/lib/logger";
import { formatDateTimeIST } from "@/lib/formatUtils";

export default function HandoverTab({ location }) {
  const [handoverSummary, setHandoverSummary] = useState(null);
  const [handoverForm, setHandoverForm] = useState({});
  const [handoverHistory, setHandoverHistory] = useState([]);
  const [handoverLoading, setHandoverLoading] = useState(false);

  const fetchHandoverSummary = useCallback(async () => {
    setHandoverLoading(true);
    try {
      const { data } = await api.get(`/handover/generate?location=${location}`);
      setHandoverSummary(data);
    } catch { toast.error("Failed to load summary"); }
    setHandoverLoading(false);
  }, [location]);

  const fetchHandoverHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/handover/history?location=${location}`);
      setHandoverHistory(data.records || []);
    } catch (err) { logger.error("Failed to fetch handover history:", err); }
  }, [location]);

  const handleSubmitHandover = async () => {
    if (!handoverForm.outgoing_name?.trim()) { toast.error("Enter your name"); return; }
    try {
      await api.post("/handover/submit", {
        location,
        shift: handoverForm.shift || "",
        outgoing_name: handoverForm.outgoing_name,
        outgoing_remarks: handoverForm.outgoing_remarks || "",
        incoming_name: handoverForm.incoming_name || "",
        summary: handoverSummary?.counts || {},
      });
      toast.success("Handover submitted. Awaiting incoming guard acknowledgement.");
      setHandoverForm({});
      setHandoverSummary(null);
      fetchHandoverHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleAcknowledgeHandover = async (id) => {
    const name = prompt("Enter your name (incoming guard):");
    if (!name?.trim()) return;
    try {
      await api.put(`/handover/${id}/acknowledge`, { incoming_name: name });
      toast.success("Handover acknowledged");
      fetchHandoverHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  useEffect(() => {
    fetchHandoverSummary();
    fetchHandoverHistory();
  }, [fetchHandoverSummary, fetchHandoverHistory]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-3">
      <Card className="lg:col-span-2 p-4" data-testid="handover-summary">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>Current Shift Summary</h3>
          <Button variant="outline" size="sm" onClick={fetchHandoverSummary} className="h-8 text-xs" data-testid="refresh-handover">
            <RotateCcw size={12} className="mr-1" /> Refresh
          </Button>
        </div>
        {handoverLoading && <p className="text-sm text-gray-400 py-4">Loading summary...</p>}
        {handoverSummary && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { label: "Visitors On-Site", count: handoverSummary.counts?.open_visitors || 0, icon: Users, color: "blue" },
                { label: "Unreturned Keys", count: handoverSummary.counts?.unreturned_keys || 0, icon: Key, color: "amber" },
                { label: "Vehicles On-Site", count: handoverSummary.counts?.active_vehicles || 0, icon: Car, color: "green" },
                { label: "Pending RGP", count: handoverSummary.counts?.pending_rgp || 0, icon: Package, color: "red" },
                { label: "Pending Job Work", count: handoverSummary.counts?.pending_jobwork || 0, icon: Package, color: "amber" },
                { label: "Employees Out", count: handoverSummary.counts?.employees_out || 0, icon: Clock, color: "blue" },
              ].map((item) => (
                <Card key={item.label} className={`p-2.5 border-l-4 ${item.count > 0 ? "border-" + item.color + "-500 bg-" + item.color + "-50/30" : "border-gray-200"}`}>
                  <div className="flex items-center gap-2">
                    <item.icon size={16} className="opacity-50" />
                    <div>
                      <div className="text-lg font-bold" style={{ fontFamily: "Chivo" }}>{item.count}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{item.label}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {handoverSummary.open_visitors?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase text-gray-500 mb-1 flex items-center gap-1"><Users size={12} /> Open Visitors</h4>
                <div className="space-y-0.5">
                  {handoverSummary.open_visitors.map((v, i) => (
                    <div key={`visitor-${v.name}-${i}`} className="text-xs flex justify-between p-1.5 bg-blue-50/50 rounded">
                      <span><strong>{v.name}</strong> ({v.company})</span>
                      <span className="text-gray-500">Meeting: {v.whom_to_meet}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {handoverSummary.unreturned_keys?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase text-gray-500 mb-1 flex items-center gap-1"><Key size={12} /> Unreturned Keys</h4>
                <div className="space-y-0.5">
                  {handoverSummary.unreturned_keys.map((k, i) => (
                    <div key={`key-${k.key_name}-${i}`} className="text-xs flex justify-between p-1.5 bg-amber-50/50 rounded">
                      <span><strong>{k.key_name}</strong> — {k.issued_to}</span>
                      <span className="text-gray-500">{k.ref_no}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {handoverSummary.active_vehicles?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase text-gray-500 mb-1 flex items-center gap-1"><Car size={12} /> Vehicles On-Site</h4>
                <div className="space-y-0.5">
                  {handoverSummary.active_vehicles.map((v, i) => (
                    <div key={`vehicle-${v.vehicle_no}-${i}`} className="text-xs flex justify-between p-1.5 bg-green-50/50 rounded">
                      <span><strong>{v.vehicle_no}</strong> — {v.driver_name}</span>
                      <span className="text-gray-500">{v.purpose}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {handoverSummary.pending_rgp?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase text-amber-600 mb-1 flex items-center gap-1"><AlertTriangle size={12} /> Pending RGP</h4>
                <div className="space-y-0.5">
                  {handoverSummary.pending_rgp.map((r, i) => (
                    <div key={`rgp-${r.ref_no}-${i}`} className="text-xs p-1.5 bg-red-50/50 rounded">
                      <strong>{r.ref_no}</strong> — {r.party_name}: {r.material}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-4" data-testid="handover-form">
        <h3 className="font-bold mb-3" style={{ fontFamily: "Chivo" }}>Sign Off</h3>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Shift</Label>
            <Select value={handoverForm.shift || ""} onValueChange={v => setHandoverForm(p => ({ ...p, shift: v }))}>
              <SelectTrigger className="h-11" data-testid="handover-shift"><SelectValue placeholder="Select shift" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="A (08:00 AM - 08:00 PM)">A (08:00 AM - 08:00 PM)</SelectItem>
                <SelectItem value="B (08:00 PM - 08:00 AM)">B (08:00 PM - 08:00 AM)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Outgoing Guard Name *</Label>
            <Input value={handoverForm.outgoing_name || ""} onChange={e => setHandoverForm(p => ({ ...p, outgoing_name: e.target.value }))} className="h-11" data-testid="outgoing-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Incoming Guard Name</Label>
            <Input value={handoverForm.incoming_name || ""} onChange={e => setHandoverForm(p => ({ ...p, incoming_name: e.target.value }))} className="h-11" data-testid="incoming-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
            <Input value={handoverForm.outgoing_remarks || ""} onChange={e => setHandoverForm(p => ({ ...p, outgoing_remarks: e.target.value }))} className="h-11" data-testid="handover-remarks" />
          </div>
          <Button onClick={handleSubmitHandover} className="w-full bg-emerald-700 hover:bg-emerald-800 h-11" data-testid="submit-handover-btn">
            <ClipboardCheck size={16} className="mr-2" /> Sign Off & Submit Handover
          </Button>
        </div>

        <div className="mt-5 pt-4 border-t">
          <h4 className="text-xs font-bold uppercase text-gray-500 mb-2">Recent Handovers</h4>
          {handoverHistory.length === 0 && <p className="text-xs text-gray-400">No handover records yet</p>}
          {handoverHistory.map((h, i) => (
            <div key={h.id || `handover-${i}`} className="text-xs border-b py-2 last:border-0">
              <div className="flex justify-between">
                <span className="font-semibold">{h.shift}</span>
                <span className={`px-1.5 py-0.5 rounded font-semibold ${h.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {h.status === "completed" ? "Completed" : "Pending Ack."}
                </span>
              </div>
              <div className="text-gray-500 mt-0.5">
                Out: {h.outgoing_name} {h.outgoing_sign_time ? `(${formatDateTimeIST(h.outgoing_sign_time)})` : ""}
              </div>
              {h.incoming_acknowledged ? (
                <div className="text-gray-500">In: {h.incoming_name} {h.incoming_sign_time ? `(${formatDateTimeIST(h.incoming_sign_time)})` : ""}</div>
              ) : (
                <Button size="sm" variant="outline" className="h-6 text-[10px] mt-1" onClick={() => handleAcknowledgeHandover(h.id)} data-testid={`ack-handover-${i}`}>
                  Acknowledge
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
